import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faSpinner,
  faCircle,
  faSync,
  faCalendarXmark,
  faCalendarPlus,
  faBell,
  faBellSlash,
  faCheck,
  faExclamationTriangle,
  faPen,
  faPencil,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "sonner";
import BranchRequiredWrapper from "@/components/banners/BranchRequiredWrapper";
import PageLoader from "@/components/loader/PageLoader";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Employee } from "@/utils/types";
import { Tooltip } from "react-tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface CalendarStatus {
  connected: boolean;
  connected_at?: string;
  last_updated?: string;
  email?: string;
}

interface WebhookStatus {
  active: boolean;
  expires_at?: string;
  calendar_id?: string;
}

const GoogleCalendarSettings: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<CalendarStatus | null>(null);
  const [webhookStatus, setWebhookStatus] = useState<WebhookStatus | null>(
    null,
  );
  const [isConnecting, setIsConnecting] = useState(false);
  const [isWebhookLoading, setIsWebhookLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const navigate = useNavigate();
  const [isFetchingStatus, setIsFetchingStatus] = useState(false);
  const [showCalendarIdModal, setShowCalendarIdModal] = useState(false);
  const [calendarIdInput, setCalendarIdInput] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
    null,
  );

  // Fetch calendar status
  const fetchCalendarStatus = async () => {
    if (!user?.access_token) {
      console.log("No access token available");
      return;
    }

    if (!user?.selectedBranch?.id) {
      console.log("No branch ID available");
      return;
    }

    if (!axiosInstance) {
      console.log("Axios instance not initialized");
      return;
    }

    try {
      setIsFetchingStatus(true);
      const response = await axiosInstance.get(
        `/api/v1/calendar/status/?branch_id=${user.selectedBranch.id}`,
      );
      console.log("Calendar status response:", response.data);
      setStatus(response.data);
    } catch (error: any) {
      console.error("Error fetching calendar status:", error);
      console.error("Error response:", error.response?.data);
      if (error.response?.status === 401) {
        toast.error("Please log in again to continue");
        // The axios interceptor will handle the logout and redirect
      } else {
        toast.error(
          error.response?.data?.message || "Failed to fetch calendar status",
        );
      }
    } finally {
      setIsFetchingStatus(false);
    }
  };

  // Fetch webhook status
  const fetchWebhookStatus = async () => {
    if (!user?.selectedBranch?.id || !axiosInstance || !status?.connected) {
      return;
    }

    try {
      setIsWebhookLoading(true);
      const response = await axiosInstance.get(
        `/api/v1/calendar/check_webhooks/?branch_id=${user.selectedBranch.id}`,
      );
      console.log("Webhook status response:", response.data);
      setWebhookStatus({
        active: response.data.webhooks_active || false,
        expires_at: response.data.expires_at,
        calendar_id: response.data.calendar_id,
      });
    } catch (error: any) {
      console.error("Error fetching webhook status:", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch webhook status",
      );
      setWebhookStatus({ active: false });
    } finally {
      setIsWebhookLoading(false);
    }
  };

  const fetchEmployees = async () => {
    if (!user?.selectedBranch?.id) {
      console.error("No branch selected");
      return;
    }
    if (!axiosInstance) {
      console.error("Axios instance not initialized");
      return;
    }
    try {
      const response = await axiosInstance.get(
        `/api/v1/employee/?branch=${user.selectedBranch.id}`,
      );
      setEmployees(response.data.results);
      console.log("Employees fetched:", response.data);
    } catch (error: any) {
      console.error("Error fetching employees:", error);
    }
  };

  useEffect(() => {
    if (user?.selectedBranch?.id) {
      fetchEmployees();
    }
  }, [user?.selectedBranch?.id, loading, axiosInstance]);

  // Disconnect calendar
  const handleDisconnect = async () => {
    if (!user?.selectedBranch?.id) {
      console.error("No branch selected");
      return;
    }

    if (!axiosInstance) {
      console.error("Axios instance not initialized");
      return;
    }

    try {
      await axiosInstance.post("/api/v1/calendar/disconnect/", {
        branch_id: user.selectedBranch.id,
      });
      toast.success("Calendar disconnected successfully");
      // Refresh the status
      fetchCalendarStatus();
    } catch (error: any) {
      console.error("Error disconnecting calendar:", error);
      toast.error(
        error.response?.data?.message || "Failed to disconnect calendar",
      );
    }
  };

  // Handle connect button click
  const handleConnect = async () => {
    if (!user?.selectedBranch?.id) {
      console.error("No branch selected");
      return;
    }

    if (!axiosInstance) {
      console.error("Axios instance not initialized");
      return;
    }

    setIsConnecting(true);
    try {
      // First get the auth URL from the backend
      const response = await axiosInstance.get(
        `/api/v1/calendar/auth/?branch_id=${user.selectedBranch.id}`,
      );

      // Then redirect to the Google auth URL
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error: any) {
      console.error("Error connecting calendar:", error);
      toast.error(
        error.response?.data?.message || "Failed to connect calendar",
      );
    } finally {
      setIsConnecting(false);
    }
  };

  // Handle webhook registration
  const handleRegisterWebhook = async () => {
    if (!user?.selectedBranch?.id || !axiosInstance) {
      return;
    }

    try {
      setIsWebhookLoading(true);
      await axiosInstance.post("/api/v1/calendar/register_webhook/", {
        branch_id: user.selectedBranch.id,
      });
      toast.success("Webhook registered successfully");
      fetchWebhookStatus();
    } catch (error: any) {
      console.error("Error registering webhook:", error);
      toast.error(
        error.response?.data?.message || "Failed to register webhook",
      );
    } finally {
      setIsWebhookLoading(false);
    }
  };

  // Handle webhook unregistration
  const handleUnregisterWebhook = async () => {
    if (!user?.selectedBranch?.id || !axiosInstance) {
      return;
    }

    try {
      setIsWebhookLoading(true);
      await axiosInstance.post("/api/v1/calendar/unregister_webhook/", {
        branch_id: user.selectedBranch.id,
        calendar_id: webhookStatus?.calendar_id,
      });
      toast.success("Webhook unregistered successfully");
      fetchWebhookStatus();
    } catch (error: any) {
      console.error("Error unregistering webhook:", error);
      toast.error(
        error.response?.data?.message || "Failed to unregister webhook",
      );
    } finally {
      setIsWebhookLoading(false);
    }
  };

  // Handle syncing events
  const handleSyncEvents = async () => {
    if (!user?.selectedBranch?.id || !axiosInstance) {
      return;
    }

    try {
      const requestData: any = {
        branch_id: user.selectedBranch.id,
      };

      // Include calendar_id if available
      if (webhookStatus?.calendar_id) {
        requestData.calendar_id = webhookStatus.calendar_id;
      }

      const response = await axiosInstance.post(
        "/api/v1/calendar/sync_events/",
        requestData,
      );

      // Check if we have synced_count directly or through results
      const syncedCount =
        response.data.synced_count ||
        (response.data.results
          ? response.data.results.created + response.data.results.updated
          : 0);

      toast.success(`Synced ${syncedCount} events successfully`);
    } catch (error: any) {
      console.error("Error syncing events:", error);
      toast.error(error.response?.data?.message || "Failed to sync events");
    }
  };

  // Handle OAuth callback
  useEffect(() => {
    const success = searchParams.get("success");
    const error = searchParams.get("error");

    if (success === "true") {
      toast.success("Calendar connected successfully");
      fetchCalendarStatus();
    } else if (error) {
      toast.error(error);
    }

    // Clear URL parameters
    const newUrl = window.location.pathname;
    window.history.replaceState({}, "", newUrl);
  }, [searchParams]);

  // Effect for handling branch changes and initial mount
  useEffect(() => {
    if (!loading && user?.selectedBranch?.id) {
      console.log("Fetching calendar status");
      fetchCalendarStatus();
    }
  }, [user?.selectedBranch?.id, loading]);

  // Fetch webhook status when calendar status changes
  useEffect(() => {
    if (status?.connected) {
      fetchWebhookStatus();
    } else {
      setWebhookStatus(null);
    }
  }, [status]);

  const handleCreateEmployeeCalendar = async (employeeId: number) => {
    if (!axiosInstance) return;
    try {
      const response = await axiosInstance.post(
        "/api/v1/calendar/create_employee_calendar/",
        {
          employee_id: employeeId,
        },
      );
      toast.success("Calendar created successfully");
      fetchEmployees(); // Refresh the employee list
    } catch (error: any) {
      console.error("Error creating employee calendar:", error);
      toast.error(error.response?.data?.message || "Failed to create calendar");
    }
  };

  const handleDeleteEmployeeCalendar = async (employeeId: number) => {
    if (!axiosInstance) return;
    try {
      const response = await axiosInstance.post(
        "/api/v1/calendar/delete_employee_calendar/",
        {
          employee_id: employeeId,
        },
      );
      toast.success("Calendar deleted successfully");
      fetchEmployees(); // Refresh the employee list
    } catch (error: any) {
      console.error("Error deleting employee calendar:", error);
      toast.error(error.response?.data?.message || "Failed to delete calendar");
    }
  };

  const handleSyncCalendars = async () => {
    if (!user?.selectedBranch?.id || !axiosInstance) return;
    try {
      const response = await axiosInstance.post(
        "/api/v1/calendar/sync_employee_calendars/",
        {
          branch_id: user.selectedBranch.id,
        },
      );
      toast.success(
        `Created ${response.data.created_count} calendars successfully`,
      );
      fetchEmployees(); // Refresh the employee list
    } catch (error: any) {
      console.error("Error syncing calendars:", error);
      toast.error(error.response?.data?.message || "Failed to sync calendars");
    }
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const handleEditEmployeeCalendar = (employeeId: number) => {
    setSelectedEmployeeId(employeeId);
    setCalendarIdInput("");
    setShowCalendarIdModal(true);
  };

  const handleAssignCalendarId = async () => {
    if (!axiosInstance || !selectedEmployeeId || !calendarIdInput.trim())
      return;

    try {
      setIsLoading(true);
      const response = await axiosInstance.post(
        "/api/v1/calendar/assign_calendar_id/",
        {
          employee_id: selectedEmployeeId,
          calendar_id: calendarIdInput.trim(),
        },
      );
      toast.success("Calendar ID assigned successfully");
      setShowCalendarIdModal(false);
      fetchEmployees(); // Refresh the employee list
    } catch (error: any) {
      console.error("Error assigning calendar ID:", error);
      toast.error(
        error.response?.data?.error || "Failed to assign calendar ID",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLoader isFetching={isFetching}>
      <BranchRequiredWrapper>
        <div className="mb-4 flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
          <h1 className="text-xl font-semibold lg:text-2xl">
            Google Calendar Integration
          </h1>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col space-y-2 rounded-lg bg-white p-4 shadow">
            <div className="flex items-center justify-between">
              {isFetchingStatus ? (
                <div className="flex items-center justify-center gap-2">
                  <FontAwesomeIcon icon={faSpinner} spin className="h-6 w-6" />
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <h1 className="font-bold lg:text-lg">Calendar Status</h1>
                  {status?.connected ? (
                    <FontAwesomeIcon
                      icon={faCircle}
                      className="h-2 w-2 animate-pulse text-green-500"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faCircle}
                      className="h-2 w-2 animate-pulse text-red-500"
                    />
                  )}
                </div>
              )}
              <div
                className={`rounded-full p-3 ${
                  status?.connected ? "bg-green-50" : "bg-red-50"
                }`}
              >
                <FontAwesomeIcon
                  icon={faCalendar}
                  className={`h-6 w-6 ${
                    status?.connected ? "text-green-500" : "text-red-500"
                  }`}
                />
              </div>
            </div>

            {isFetchingStatus ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  className="mr-2 text-red-500"
                />
                <span className="text-sm text-gray-600">Loading...</span>
              </>
            ) : status?.connected ? (
              <div className="space-y-4">
                <div className="items-center">
                  {status.email && (
                    <p className="text mt-1 text-gray-700">
                      Connected account:{" "}
                      <span className="font-medium">{status.email}</span>
                    </p>
                  )}
                  {status.connected_at && (
                    <p className="mt-1 text-sm text-gray-600">
                      Connected since:{" "}
                      {new Date(status.connected_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <Button
                  variant="destructive"
                  onClick={handleDisconnect}
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  ) : (
                    "Disconnect Calendar"
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="items-center">
                  <div>
                    <p className="text font-medium text-gray-700">
                      Calendar is not connected
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      Connect your Google Calendar to sync appointments
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleConnect}
                  className="w-full bg-red-500 text-white hover:bg-red-600"
                  disabled={isConnecting}
                >
                  {isConnecting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                      Connecting...
                    </>
                  ) : (
                    "Connect Calendar"
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Webhook Management Section */}
          {status?.connected && (
            <div className="flex flex-col space-y-2 rounded-lg bg-white p-4 shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center justify-center gap-2">
                  <h1 className="font-bold lg:text-lg">Webhook Status</h1>
                  {webhookStatus?.active ? (
                    <FontAwesomeIcon
                      icon={faCircle}
                      className="h-2 w-2 animate-pulse text-green-500"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faCircle}
                      className="h-2 w-2 animate-pulse text-red-500"
                    />
                  )}
                </div>
                <div
                  className={`rounded-full p-3 ${
                    webhookStatus?.active ? "bg-green-50" : "bg-red-50"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={webhookStatus?.active ? faBell : faBellSlash}
                    className={`h-6 w-6 ${
                      webhookStatus?.active ? "text-green-500" : "text-red-500"
                    }`}
                  />
                </div>
              </div>

              {isWebhookLoading ? (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    className="text-red-500"
                  />
                  <span className="text-sm text-gray-600">
                    Loading webhook status...
                  </span>
                </div>
              ) : webhookStatus?.active ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">
                      Webhook is active. You will receive real-time
                      notifications for calendar changes.
                    </p>
                    {webhookStatus.expires_at && (
                      <p className="mt-1 text-sm text-gray-600">
                        Expires: {formatDate(webhookStatus.expires_at)}
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="default"
                      onClick={handleSyncEvents}
                      className="flex-1"
                    >
                      <FontAwesomeIcon icon={faSync} className="mr-2" />
                      Sync Events
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleUnregisterWebhook}
                      className="flex-1"
                    >
                      <FontAwesomeIcon icon={faBellSlash} className="mr-2" />
                      Disable Notifications
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    No active webhook. Enable notifications to receive real-time
                    updates when calendar events change.
                  </p>
                  <Button
                    variant="default"
                    onClick={handleRegisterWebhook}
                    className="w-full"
                  >
                    <FontAwesomeIcon icon={faBell} className="mr-2" />
                    Enable Notifications
                  </Button>
                </div>
              )}
            </div>
          )}

          {status?.connected && (
            <div className="flex flex-col space-y-2 rounded-lg bg-white p-4 shadow">
              <div className="mb-4 flex items-center justify-between">
                <h1 className="text-lg font-semibold">Employees</h1>
                <Tooltip
                  id="sync-calendar-tooltip"
                  content="Creates a calendar for each employee who does not have one"
                />
                <Button
                  variant="outline"
                  onClick={handleSyncCalendars}
                  className="flex items-center gap-2"
                  data-tooltip-id="sync-calendar-tooltip"
                  data-tooltip-place="top"
                >
                  <FontAwesomeIcon icon={faSync} className="h-4 w-4" />
                  Sync Calendar
                </Button>
              </div>
              <div className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Job Title</TableHead>
                      <TableHead>Calendar</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((employee) => (
                      <TableRow key={employee.id}>
                        <TableCell>
                          {employee.first_name} {employee.last_name}
                        </TableCell>
                        <TableCell>
                          {(employee.job_title ?? "").charAt(0).toUpperCase() +
                            (employee.job_title ?? "").slice(1).toLowerCase()}
                        </TableCell>
                        <TableCell>
                          {employee.calendar_id ? "Connected" : "Not connected"}
                        </TableCell>
                        <TableCell className="text-center">
                          {employee.calendar_id ? (
                            <>
                              <Tooltip
                                id="delete-calendar-tooltip"
                                content="Delete calendar"
                              />
                              <Button
                                variant="ghost"
                                className="text-red-500"
                                onClick={() =>
                                  handleDeleteEmployeeCalendar(employee.id || 0)
                                }
                                data-tooltip-id="delete-calendar-tooltip"
                                data-tooltip-place="top"
                              >
                                <FontAwesomeIcon
                                  icon={faCalendarXmark}
                                  className="text-red-500"
                                />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Tooltip
                                id="create-calendar-tooltip"
                                content="Create calendar"
                              />
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  handleCreateEmployeeCalendar(employee.id || 0)
                                }
                                className="text-green-500"
                                data-tooltip-id="create-calendar-tooltip"
                                data-tooltip-place="top"
                              >
                                <FontAwesomeIcon
                                  icon={faCalendarPlus}
                                  className="text-green-500"
                                />
                              </Button>

                              <Tooltip
                                id="edit-calendar-tooltip"
                                content="Add calendar id"
                              />
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  handleEditEmployeeCalendar(employee.id || 0)
                                }
                                data-tooltip-id="edit-calendar-tooltip"
                                data-tooltip-place="top"
                              >
                                <FontAwesomeIcon
                                  icon={faPen}
                                  className="text-green-500"
                                />
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </BranchRequiredWrapper>

      {showCalendarIdModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
            <button
              onClick={() => setShowCalendarIdModal(false)}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2 className="mb-2 text-xl font-semibold">Add Calendar ID</h2>
            <p className="mb-4 text-gray-600">
              Enter the Google Calendar ID for this employee
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="calendar-id">Calendar ID</Label>
                <Input
                  id="calendar-id"
                  value={calendarIdInput}
                  onChange={(e) => setCalendarIdInput(e.target.value)}
                  placeholder="Enter calendar ID"
                  className="w-full"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowCalendarIdModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleAssignCalendarId}
                disabled={isLoading || !calendarIdInput.trim()}
                className="ml-2"
              >
                {isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageLoader>
  );
};

export default GoogleCalendarSettings;
